import { useAuthStore } from '@/stores/auth-store'
import { useGlobalStore } from '@/stores/global-store'
import { ref } from 'vue'

import { UsableFetch, UsableStringFetch } from './types'

export function useFetchWithInterceptor<T>(url: RequestInfo, options?: RequestInit): UsableFetch<T> {
  const authStore = useAuthStore()
  const globalStore = useGlobalStore()
  const response = ref<T>()

  // Function for creating headers
  const createHeaders = (): Headers => {
    const headers = new Headers(options?.headers || {})
    headers.set('Access-Control-Allow-Headers', 'Content-Type')
    headers.set('Access-Control-Allow-Origin', '*')
    headers.set('Content-Type', 'application/json')

    const token = authStore.token
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    headers.set('X-Language', globalStore.language || 'en')

    return headers
  }

  const request = async (retry = true): Promise<void> => {
    const headers = createHeaders()
    globalStore.startLoading()

    try {
      const res = await fetch(url, {
        ...options,
        headers: headers,
      })
      if (!res.ok) {
        if (res.status === 401 && retry) {
          try {
            await authStore.refreshAccessToken() // Attempt to refresh the token
            await request(false) // Retrying the request with a new token
            return
          } catch (error) {
            await authStore.logout()
            throw new Error('Unauthorized')
          }
        }

        const data = await res.json()
        if (data.errors && typeof data.errors === 'object') {
          if (Array.isArray(data.errors)) {
            throw new Error(displayErrorFromArray(data.errors))
          } else {
            throw new Error(displayError(data.errors))
          }
        } else if (data.detail) {
          throw new Error(data.detail)
        } else {
          throw new Error(`${res.status} ${res.statusText}`)
        }
      }

      const text = await res.text()
      if (text) {
        response.value = JSON.parse(text)
      } else {
        response.value = {} as T
      }
    } catch (error: any) {
      throw new Error(error.toString().replace('Error: ', ''))
    } finally {
      globalStore.stopLoading()
    }
  }

  return { response, request }
}

export function useFetchWithInterceptorForPlainText(url: RequestInfo, options?: RequestInit): UsableStringFetch {
  const authStore = useAuthStore()
  const globalStore = useGlobalStore()
  const response = ref<string | undefined>('')

  // Function for creating headers
  const createHeaders = (options?: RequestInit): Headers => {
    const headers = new Headers(options?.headers || {})
    headers.set('Access-Control-Allow-Headers', 'Content-Type')
    headers.set('Access-Control-Allow-Origin', '*')
    headers.set('Content-Type', 'application/json')

    const token = authStore.token
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    const existingLanguage = new Headers(options?.headers || {}).get('X-Language')

    if (existingLanguage) {
      headers.set('X-Language', existingLanguage)
    } else {
      headers.set('X-Language', globalStore.language || 'en')
    }

    return headers
  }

  const request = async (retry = true): Promise<void> => {
    const headers = createHeaders(options)
    globalStore.startLoading()
    const res = await fetch(url, {
      ...options,
      headers: headers,
    })
    if (!res.ok) {
      if (res.status === 401 && retry) {
        try {
          await authStore.refreshAccessToken() // Attempt to refresh the token
          await request(false) // Retrying the request with a new token
          return
        } catch (error) {
          await authStore.logout()
          throw new Error('Unauthorized')
        }
      }
      const data = await res.json()

      if (data.errors && typeof data.errors === 'object') {
        if (Array.isArray(data.errors)) {
          throw new Error(displayErrorFromArray(data.errors))
        } else {
          throw new Error(displayError(data.errors))
        }
      } else if (data.detail) {
        throw new Error(data.detail)
      } else {
        throw new Error(`${res.status} ${res.statusText}`)
      }
    }
    try {
      response.value = await res.text()
    } catch (error: any) {
      throw new Error(error)
    } finally {
      globalStore.stopLoading()
    }
  }

  return { response, request }
}

export function useFetchWithInterceptorForFile(
  route: RequestInfo,
  fileName: string,
  options?: RequestInit,
): { response: any; request: () => Promise<void> } {
  const authStore = useAuthStore()
  const globalStore = useGlobalStore()
  const response = ref<Blob | null>(null)
  globalStore.startLoading()

  // Function for creating headers
  const createHeaders = (): Headers => {
    const headers = new Headers(options?.headers || {})
    headers.set('Access-Control-Allow-Headers', 'Content-Type')
    headers.set('Access-Control-Allow-Origin', '*')

    const token = authStore.token
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    headers.set('x-language', globalStore.language || 'en')

    return headers
  }

  const request = async (retry = true): Promise<void> => {
    const headers = createHeaders()
    const res = await fetch(route, {
      ...options,
      headers: headers,
    })
    if (!res.ok || res.status === 204) {
      if (res.status === 401 && retry) {
        try {
          await authStore.refreshAccessToken() // Attempt to refresh the token
          await request(false) // Retrying the request with a new token
          return
        } catch (error) {
          await authStore.logout()
          throw new Error('Unauthorized')
        }
      }

      throw new Error(`${res.status} ${res.statusText}`)
    }

    try {
      // Handle file download
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      response.value = blob // Optionally store the blob if needed
    } catch (error: any) {
      throw new Error(error)
    } finally {
      globalStore.stopLoading()
    }
  }

  return { response, request }
}

function displayError(errors: any) {
  for (const key in errors) {
    if (Object.prototype.hasOwnProperty.call(errors, key)) {
      const errorMessage = errors[key] && errors[key].length > 0 ? errors[key][0] : ''
      return `${errorMessage}`
    }
  }
}

function displayErrorFromArray(errors: string[]) {
  return errors.join('. ')
}
